/** @format */

import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  computed,
  effect,
  model,
  signal,
} from '@angular/core';
import { HmfActionTextComponent, HmfTextInputComponent } from '@dsg-tech/homefield-angular';

import { FilterOptionComponent } from '../filter-option/filter-option.component';
import { FacetVOValue } from '@plp/data-access/interfaces';
import { FormsModule } from '@angular/forms';
import { StandardFilterComponent } from '../standard-filter/standard-filter.component';

type BrandFilterRow =
  | { type: 'header'; label: string }
  | {
      type: 'option';
      option: FacetVOValue;
      ariaLabel: string;
      displayValue: string;
      isLast: boolean;
    };

@Component({
  selector: 'plp-components-brand-filter',
  imports: [FormsModule, FilterOptionComponent, HmfActionTextComponent, HmfTextInputComponent],
  templateUrl: './brand-filter.component.html',
  styleUrl: './brand-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BrandFilterComponent extends StandardFilterComponent implements OnDestroy {
  private readonly regexForLettersOnly = /[A-Za-z]/;
  private readonly chunkSize = 20;
  private frameId: number | null = null;
  private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly hasHydratedExpandedList = signal(false);
  private readonly visibleRowCount = signal(0);

  constructor() {
    super();

    // In exposed-filter mode (allowCollapsing=false), there is no View All click path,
    // so we bootstrap chunk hydration here when the popover first renders.
    effect(() => {
      const totalRows = this.groupedBrandRows().length;
      const shouldHydrate =
        !this.allowCollapsing() &&
        totalRows > 0 &&
        !this.hasHydratedExpandedList() &&
        this.visibleRowCount() === 0;

      if (!shouldHydrate) {
        return;
      }

      this.visibleRowCount.set(Math.min(this.chunkSize, totalRows));
      this.stopProgressiveHydration();
      this.scheduleProgressiveHydration(totalRows);
    });
  }

  ngOnDestroy(): void {
    this.stopProgressiveHydration();
    if (this.searchDebounceTimer !== null) {
      clearTimeout(this.searchDebounceTimer);
    }
  }

  brandFilterString = model('');

  // Indicates whether the brand list has more than 10 options.
  hasOverflow = computed<boolean>(() => (this.filterOptions()?.length ?? 0) > 10);

  // Returns the top 10 brands by product count (or all when <= 10).
  topFilterOptions = computed<FacetVOValue[]>(() => {
    const options = this.filterOptions() ?? [];
    if (options.length <= 10) {
      return options;
    }

    return [...options].sort((optionA, optionB) => optionB.count - optionA.count).slice(0, 10);
  });

  // Maps a brand value to its original index for fast label/value lookup.
  optionIndexByValue = computed<Map<string, number>>(() => {
    const valueIndexMap = new Map<string, number>();
    (this.filterOptions() ?? []).forEach((option, index) => {
      valueIndexMap.set(option.value, index);
    });

    return valueIndexMap;
  });

  // Filters brand options by the current search text.
  filteredOptionsResult = computed(() => {
    const filterString = this.brandFilterString().toLowerCase();
    return this.filterOptions()?.filter((option) =>
      option.value.toLowerCase().includes(filterString),
    );
  });

  // Shows alphabetical rows when expanded or when searching.
  shouldRenderAlphabeticalList = computed<boolean>(() => {
    return this.hasHydratedExpandedList() || this.showMore() || this.brandFilterString().length > 0;
  });

  // Hides mounted alphabetical rows while collapsed after first hydration.
  shouldHideHydratedList = computed<boolean>(() => {
    return this.hasHydratedExpandedList() && !this.showMore() && !this.brandFilterString();
  });

  // Limits rendered rows during first expand, then shows all rows afterward.
  visibleGroupedBrandRows = computed<BrandFilterRow[]>(() => {
    const rows = this.groupedBrandRows();
    if (this.hasHydratedExpandedList()) {
      return rows;
    }

    return rows.slice(0, this.visibleRowCount());
  });

  // Builds alphabetical header rows plus option rows for the expanded list.
  groupedBrandRows = computed<BrandFilterRow[]>(() => {
    const rows: BrandFilterRow[] = [];
    const filteredOptions = this.filteredOptionsResult() ?? [];

    filteredOptions.forEach((filterOption, index) => {
      const currentFirstLetter = this.getFirstLetter(filterOption.value);
      const previousFirstLetter = this.getFirstLetter(filteredOptions[index - 1]?.value);

      if (
        (index === 0 || previousFirstLetter !== currentFirstLetter) &&
        this.regexForLettersOnly.test(currentFirstLetter)
      ) {
        rows.push({ type: 'header', label: currentFirstLetter.toUpperCase() });
      }

      if (index === 0 && !this.regexForLettersOnly.test(currentFirstLetter)) {
        rows.push({ type: 'header', label: '#' });
      }

      rows.push({
        type: 'option',
        option: filterOption,
        ariaLabel: this.getAriaLabel(filterOption),
        displayValue: this.getDisplayValue(filterOption),
        isLast: index === filteredOptions.length - 1,
      });
    });

    return rows;
  });

  // Resolves the aria label for a brand option from the precomputed index map.
  getAriaLabel(filterOption: FacetVOValue): string {
    const optionIndex = this.optionIndexByValue().get(filterOption.value);
    if (optionIndex === undefined) {
      return '';
    }

    return String(this.ariaLabels()?.[optionIndex]);
  }

  // Resolves the display label for a brand option from the precomputed index map.
  getDisplayValue(filterOption: FacetVOValue): string {
    const optionIndex = this.optionIndexByValue().get(filterOption.value);
    if (optionIndex === undefined) {
      return '';
    }

    return String(this.displayValues()?.[optionIndex]);
  }

  trackRow(row: BrandFilterRow): string {
    // Provides a stable track key for header and option rows.
    if (row.type === 'header') {
      return `header-${row.label}`;
    }

    return this.trackFilterName(row.option);
  }

  // Progressively hydrates rows as the user types, deferring the full render until typing pauses.
  brandInputChanged(): void {
    // Cancel any in-flight hydration and pending debounce from the previous keystroke.
    this.stopProgressiveHydration();
    if (this.searchDebounceTimer !== null) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }

    this.showMore.set(true);
    this.hasHydratedExpandedList.set(false);

    const totalRows = this.groupedBrandRows().length;

    // Show the first chunk immediately for instant visual feedback.
    this.visibleRowCount.set(Math.min(this.chunkSize, totalRows));

    if (totalRows <= this.chunkSize) {
      // All rows fit in the first chunk — no further hydration needed.
      this.hasHydratedExpandedList.set(true);
    } else {
      // Defer remaining rows until the user pauses typing.
      this.searchDebounceTimer = setTimeout(() => {
        this.searchDebounceTimer = null;
        this.scheduleProgressiveHydration(totalRows);
      }, 300);
    }

    this.showMoreChange.emit(this.showMore());
  }

  // Toggles expanded state and progressively hydrates rows on first expand.
  override toggleShowMore(): void {
    if (this.showMore()) {
      this.stopProgressiveHydration();
      this.showMore.set(false);
      this.showMoreChange.emit(this.showMore());
      return;
    }

    this.showMore.set(true);
    this.showMoreChange.emit(this.showMore());

    const totalRows = this.groupedBrandRows().length;
    if (totalRows === 0) {
      return;
    }

    if (this.hasHydratedExpandedList()) {
      this.visibleRowCount.set(totalRows);
      return;
    }

    this.visibleRowCount.set(Math.min(this.chunkSize, totalRows));
    this.stopProgressiveHydration();
    this.scheduleProgressiveHydration(totalRows);
  }

  // Returns the first character of a brand value in lowercase.
  private getFirstLetter(filterValue?: string): string {
    return filterValue?.[0]?.toLowerCase() ?? '';
  }

  // Adds rows in animation-frame chunks.
  private scheduleProgressiveHydration(totalRows: number): void {
    // Guard against double-scheduling: if a hydration loop is already running, do nothing.
    if (this.frameId !== null) {
      return;
    }

    // SSR / non-browser fallback: rAF is unavailable, so render everything synchronously.
    if (typeof requestAnimationFrame !== 'function') {
      this.visibleRowCount.set(totalRows);
      this.hasHydratedExpandedList.set(true);
      return;
    }

    // Each call to hydrateChunk appends one chunk of rows and reschedules itself until
    // all rows are rendered. Spreading the work across frames keeps the main thread
    // responsive between paints.
    const hydrateChunk = () => {
      // If the list was collapsed mid-flight, abort rather than rendering off-screen rows.
      if (!this.showMore()) {
        this.stopProgressiveHydration();
        return;
      }

      const currentTotalRows = this.groupedBrandRows().length;
      const nextCount = Math.min(this.visibleRowCount() + this.chunkSize, currentTotalRows);
      this.visibleRowCount.set(nextCount);

      // Once all rows are visible, mark hydration complete and stop scheduling.
      if (nextCount >= currentTotalRows) {
        this.hasHydratedExpandedList.set(true);
        this.stopProgressiveHydration();
        return;
      }

      // More rows remain — schedule the next chunk on the following frame.
      this.frameId = requestAnimationFrame(hydrateChunk);
    };

    this.frameId = requestAnimationFrame(hydrateChunk);
  }

  // Cancels any pending chunked hydration frame.
  private stopProgressiveHydration(): void {
    if (this.frameId === null) {
      return;
    }

    if (typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(this.frameId);
    }

    this.frameId = null;
  }
}
